import { Suspense } from 'react';
import { lazyPage } from '@/lib/chunkReload';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
// Subscriptions removed; manage in Donations/Stripe portal
import { Toaster } from 'sonner';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useCanonicalSync } from '@/hooks/usePageMeta';
import { useTabSync } from '@/hooks/useTabSync';
import ScrollToTop from '@/components/ScrollToTop';
import ConsentBanner from '@/components/ConsentBanner';
import CreatorPublishFeedbackPrompt from '@/components/dashboard/CreatorPublishFeedbackPrompt';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

// Route-level code splitting: every route below except Home is loaded on demand.
// Before this, App.tsx statically imported every page (including the Stripe
// checkout UI, the TipTap content editor, and the full admin/creator/member
// dashboards), so a visitor to "/" downloaded all of it before first paint —
// PageSpeed Insights measured 16.9s mobile LCP on the homepage as a result.
const SignUp = lazyPage(() => import('./pages/SignUp'));
const SignIn = lazyPage(() => import('./pages/SignIn'));
const SignUpCreator = lazyPage(() => import('./pages/SignUpCreator'));
const MissionPage = lazyPage(() => import('./pages/MissionPage'));
const DemoMissionPage = lazyPage(() => import('./pages/DemoMissionPage'));
const AuthEmailHandler = lazyPage(() => import('./pages/AuthEmailHandler'));
const PasswordReset = lazyPage(() => import('./pages/PasswordReset'));
const EmailSignIn = lazyPage(() => import('./pages/EmailSignIn'));
const UserSettings = lazyPage(() => import('./pages/dashboard/UserSettings'));
const Donations = lazyPage(() => import('./pages/dashboard/Donations'));
const DashboardHome = lazyPage(() => import('./pages/dashboard/DashboardHome'));
const MemberDashboard = lazyPage(() => import('./components/dashboard/MemberDashboard'));
const DashboardLayout = lazyPage(() => import('./components/dashboard/DashboardLayout'));
const MyPage = lazyPage(() => import('./pages/dashboard/MyPage'));
const Membership = lazyPage(() => import('./pages/dashboard/Membership'));
const DonationsPage = lazyPage(() => import('@/components/donations/DonationsPage'));
const AudienceUsersPage = lazyPage(() => import('@/components/audience/AudienceUsersPage'));
const AdminDashboard = lazyPage(() => import('@/pages/admin/Dashboard'));
const Users = lazyPage(() => import('@/pages/admin/Users'));
const Compliance = lazyPage(() => import('@/pages/admin/Compliance'));
const CreatorContentList = lazyPage(() => import('./pages/creator/content/index'));
const AddContentPage = lazyPage(() => import('./pages/creator/content/add'));
const ContentDetail = lazyPage(() => import('./pages/creator/content/[contentId]'));
const EditContent = lazyPage(() => import('./pages/creator/content/edit'));
const Privacy = lazyPage(() => import('./pages/Privacy'));
const Terms = lazyPage(() => import('./pages/Terms'));
const AcceptableUse = lazyPage(() => import('./pages/AcceptableUse'));
const Resources = lazyPage(() => import('./pages/Resources'));
const GivingWithPurpose = lazyPage(() => import('./pages/resources/GivingWithPurpose'));
const SmartGivingGuide = lazyPage(() => import('./pages/resources/SmartGivingGuide'));
const StrategicStewardship = lazyPage(() => import('./pages/resources/StrategicStewardship'));
const SupportingLocalChurch = lazyPage(() => import('./pages/resources/SupportingLocalChurch'));
const Contact = lazyPage(() => import('./pages/Contact'));
const HowItWorks = lazyPage(() => import('./pages/HowItWorks'));
const BestPractices = lazyPage(() => import('./pages/BestPractices'));
const About = lazyPage(() => import('./pages/About'));
const ParentPlusCalculator = lazyPage(() => import('./pages/ParentPlusCalculator'));
const Blog = lazyPage(() => import('./pages/Blog'));
const BlogPost = lazyPage(() => import('./pages/blog/BlogPost'));
const Roadmap = lazyPage(() => import('./pages/Roadmap'));
const PaymentSuccess = lazyPage(() => import('./pages/payment/PaymentSuccess'));
const PaymentCancel = lazyPage(() => import('./pages/payment/PaymentCancel'));
const NotFound = lazyPage(() => import('./pages/NotFound'));

export default function App() {
  // Navigating away from a crashed page clears the error screen.
  const { pathname } = useLocation();
  usePageTracking();
  useTabSync();
  useCanonicalSync();
  return (
    <>
      <ScrollToTop />
      <Toaster />
      <ConsentBanner />
      <CreatorPublishFeedbackPrompt />
      <AppErrorBoundary key={pathname}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/best-practices" element={<BestPractices />} />
        <Route path="/faqs" element={<BestPractices />} />
        <Route path="/about" element={<About />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/parent-plus" element={<ParentPlusCalculator />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/pricing" element={<Navigate to="/faqs" replace />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/funding-your-education" element={<GivingWithPurpose />} />
        <Route path="/resources/smart-fundraising-guide" element={<SmartGivingGuide />} />
        <Route path="/resources/engaging-your-supporters" element={<StrategicStewardship />} />
        <Route path="/resources/supporting-a-student" element={<SupportingLocalChurch />} />
        {/* Legacy routes for old resource URLs */}
        <Route path="/resources/giving-with-purpose" element={<Navigate to="/resources/funding-your-education" replace />} />
        <Route path="/resources/smart-giving-guide" element={<Navigate to="/resources/smart-fundraising-guide" replace />} />
        <Route path="/resources/strategic-stewardship" element={<Navigate to="/resources/engaging-your-supporters" replace />} />
        <Route path="/resources/supporting-your-local-church" element={<Navigate to="/resources/supporting-a-student" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup-creator" element={<SignUpCreator />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/email-signin" element={<EmailSignIn />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/fund/demo" element={<DemoMissionPage />} />
        <Route path="/fund/:creatorId" element={<MissionPage />} />
        {/* Legacy route - redirect /mission/ to /fund/ */}
        <Route path="/mission/:creatorId" element={<MissionPage />} />
        <Route path="/auth/email-handler" element={<AuthEmailHandler />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />

        {/* Protected Member Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute userType="member">
              <MemberDashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="settings" element={<UserSettings />} />
          <Route path="donations" element={<Donations />} />
          {/* Subscriptions route removed */}
          <Route path="fund/:creatorId" element={<MissionPage />} />
          <Route path="mission/:creatorId" element={<MissionPage />} />
        </Route>

        {/* Protected Creator Dashboard Routes */}
        <Route
          path="/creator"
          element={
            <ProtectedRoute userType="creator">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyPage />} />
          <Route path="settings" element={<UserSettings />} />
          {/* Payment settings page removed; onboarding handled in-app via Stripe links */}
          {/* NESTED CONTENT ROUTES */}
          <Route path="content" element={<Outlet />}>
            <Route index element={<CreatorContentList />} />
            <Route path="add" element={<AddContentPage />} />
            <Route path=":contentId/edit" element={<EditContent />} />
            <Route path=":contentId" element={<ContentDetail />} />
          </Route>
          <Route path="membership" element={<Membership />} />
          <Route path="donations" element={<DonationsPage />} />
          <Route path="audience" element={<AudienceUsersPage />} />
        </Route>

        {/* Protected Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        >
          <Route index element={<Users />} />
          <Route path="users" element={<Users />} />
          <Route path="compliance" element={<Compliance />} />
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </AppErrorBoundary>
    </>
  );
}
